var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import Moment from 'moment';
import * as R from 'ramda';
import { isFormElement } from '../../models/form.types';
import { findElementWith } from './def.utils';
import { mapStringPath } from './_.utils';
import { loggerElement2Str } from '../log/xbpm-logger';
import { XbpmContext } from '../context';
import { isCancelation } from './result.utils';
// create element formula action logger
var createFormulaLog = function (formulaName, element, formulaLogger) { return function (action, ctx, disabled) {
    if (disabled) {
        return;
    }
    switch (action) {
        case 'execute':
            return formulaLogger.logInfo("Formula \"" + formulaName + "\" execute with argument:", ctx);
        case 'result-ok':
            return formulaLogger.logInfo("Formula \"" + formulaName + "\" result:", ctx);
        case 'result-error':
        case 'format-error': {
            if (isCancelation(ctx)) {
                return;
            }
            var message = action === 'result-error'
                ? "Formula \"" + formulaName + "\" execution error, element " + loggerElement2Str(element) + ":"
                : "Formula \"" + formulaName + "\" format error, element " + loggerElement2Str(element) + ":";
            formulaLogger.logError(message, ctx);
            console.error(message, ctx);
        }
    }
}; };
// create element formula
export var createFormula = R.curry(function (formulaName, element, path, env, formulaStr) {
    if (typeof formulaStr !== 'string' || !formulaStr) {
        return undefined;
    }
    // element formula logger
    var formulaLogger = env.formLogger({ formulaName: formulaName, elementPath: mapStringPath(path) });
    // formula info logger
    var log = createFormulaLog(formulaName, element, formulaLogger);
    try {
        var formulaFn_1 = Function("\"use strict\";\n return (" + formulaStr + ");")();
        return function (ctx, disableLog, ignoreErr) {
            try {
                log('execute', ctx, disableLog);
                // init formula tools
                var tools = isFormElement(ctx) ? initFormulaTools(ctx) : null;
                // fire formula
                var result = formulaFn_1(ctx, tools, formulaLogger);
                if (result instanceof Promise) {
                    // async result
                    return result.then(R.tap(function (res) { return log('result-ok', res, disableLog); }), R.pipe(R.tap(function (err) { return log('result-error', err, disableLog); }), function (err) { return (ignoreErr ? null : Promise.reject(err)); }));
                }
                // sync result
                log('result-ok', result, disableLog);
                return result;
            }
            catch (err) {
                log('result-error', err, disableLog);
                if (!ignoreErr) {
                    throw err;
                }
                return null;
            }
        };
    }
    catch (err) {
        log('format-error', err, false);
        return undefined;
    }
});
// exec formula
export var execFormula = function (formula, element, disableLog, ignoreErr) {
    if (disableLog === void 0) { disableLog = false; }
    if (ignoreErr === void 0) { ignoreErr = true; }
    return typeof formula === 'function' ? formula(element, disableLog, ignoreErr) : null;
};
// exec formula async
export var execFormulaAsync = function (formula, element, disableLog, ignoreErr) {
    if (disableLog === void 0) { disableLog = false; }
    if (ignoreErr === void 0) { ignoreErr = true; }
    return __awaiter(void 0, void 0, void 0, function () {
        var execution, result;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    execution = execFormula(formula, element, disableLog, ignoreErr);
                    return [4 /*yield*/, (execution instanceof Promise ? execution : Promise.resolve(execution))];
                case 1:
                    result = _a.sent();
                    return [2 /*return*/, result];
            }
        });
    });
};
// formula tools
export var initFormulaTools = function (element) { return ({
    R: R,
    Moment: Moment,
    getAuthService: function () { return XbpmContext.getContext().authService; },
    getProvider: function (name) { return XbpmContext.getContext().getProvider(name); },
    findBlock: function (code, block) {
        return findElementWith(R.allPass([R.propEq('code', code), function (it) { return it.isBlock(); }]), block || element.rootBlock);
    },
    findField: function (code, block) {
        return findElementWith(R.allPass([R.propEq('code', code), function (it) { return it.isField(); }]), block);
    },
}); };
//# sourceMappingURL=formula.utils.js.map